/**
 * Auth Slice
 * 
 * Purpose: Manages authentication state in Redux
 * Note: Sensitive data (tokens) are stored in secure storage, not Redux
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'
import { User } from '@/types'
import { getCurrentUser, getAuthToken, isAuthenticated } from '@/core/auth'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

// Initialize from secure storage
const currentUser = getCurrentUser()
const currentToken = getAuthToken()
const authenticated = isAuthenticated()

const initialState: AuthState = {
  user: currentUser,
  token: currentToken,
  isAuthenticated: authenticated,
  isLoading: false,
  error: null,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
      state.isLoading = false
      state.error = null
    },
    clearCredentials: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.isLoading = false
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
  },
  extraReducers: (builder) => {
    // Reset isLoading on rehydration (when app loads from persisted state)
    builder.addCase(REHYDRATE, (state) => {
      state.isLoading = false
    })
  },
})

export const { setCredentials, clearCredentials, setLoading, setError, updateUser } =
  authSlice.actions

export default authSlice.reducer
